import React, { useState } from 'react';
import {
  KatRow,
  KatAlert,
  KatDropdown,
  KatPopover,
  KatIcon,
  KatLink,
} from '@amzn/katal-react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import './SourceReview.scss';
import { useLocation } from 'react-router-dom';
import OpenReviewsTable from 'src/components/SourceReview/OpenReviews/OpenReviewsTable';
import CompletedReviewsTable from 'src/components/SourceReview/CompletedReviews/CompletedReviewsTable';
import { ApprovalStatus } from 'src/generated/graphql';

type Props = {
  id: string;
};

enum ReviewStatus {
  OPEN = 'Open',
  COMPLETED = 'Completed',
}

/**
 * Source Review Page
 *
 * The Source Review page is for displaying and managing authorized reviews for a reviewer.
 * Allows users to switch between open and completed reviews.
 *
 */
const SourceReview: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();

  // Check location state to show success alert after submitting for translation
  const location = useLocation();
  const state = location.state as {
    approvalStatus?: ApprovalStatus;
    messageGroupName?: string;
  };

  // State to control visibility of success alert banner
  const [showSuccessAlert, setShowSuccessAlert] = useState(
    state?.approvalStatus === ApprovalStatus.StepOneApproved ||
      state?.approvalStatus === ApprovalStatus.StepTwoApproved ||
      state?.approvalStatus === ApprovalStatus.Approved
  );
  const [messageGroupName] = useState(state?.messageGroupName);
  const [approvalStatus] = useState(state?.approvalStatus);

  // State to track selected review status (Defaults to Open Reviews)
  const [reviewStatus, setReviewStatus] = useState(ReviewStatus.OPEN);

  // Get the page title based on selected review status (Defaults to Open Reviews)
  const getTitle = () => {
    if (reviewStatus === ReviewStatus.COMPLETED) {
      return t('source-review-history-page-title');
    }
    return t('source-review-page-title');
  };

  const getTitleToolTip = () => {
    if (reviewStatus === ReviewStatus.COMPLETED) {
      return '';
    }
    return (
      <KatPopover
        katAriaBehavior="tooltip"
        position="right"
        triggerType="hover"
        variant="info"
      >
        <KatIcon slot="trigger" size="small" name="info-circle"></KatIcon>
        <KatLink
          href="https://w.amazon.com/bin/view/INTech/Panther/docs/SourceReview"
          label={t('source-review-page-title-tooltip')}
          variant="link"
          target="_blank"
        ></KatLink>
      </KatPopover>
    );
  };

  // Get the page description based on selected review status (Defaults to Open Reviews)
  const getDescription = () => {
    if (reviewStatus === ReviewStatus.COMPLETED) {
      return t('source-review-history-page-description');
    }
    return t('source-review-page-description');
  };

  // Render the reviews table based on selected review status (Defaults to Open Reviews)
  const renderReviewsTable = () => {
    if (reviewStatus === ReviewStatus.COMPLETED) {
      return <CompletedReviewsTable id={props.id} />;
    }
    return <OpenReviewsTable id={props.id} />;
  };

  // KatDropdown options for review status selection
  const REVIEW_STATUS_OPTIONS = [
    {
      value: ReviewStatus.OPEN,
      name: t('source-review-status-dropdown-option-open'),
    },
    {
      value: ReviewStatus.COMPLETED,
      name: t('source-review-status-dropdown-option-completed'),
    },
  ];

  return (
    <section id={props.id}>
      {/* Success alert shown after submitting for translation or first step approval */}
      {showSuccessAlert && (
        <KatAlert
          className="submit-for-translation-success-message"
          header={t(
            approvalStatus === ApprovalStatus.StepOneApproved
              ? 'source-review-first-step-approve-success-header'
              : 'source-review-submit-for-translation-success-header'
          )}
          description={t(
            approvalStatus === ApprovalStatus.StepOneApproved
              ? 'source-review-first-step-approve-success-description'
              : 'source-review-submit-for-translation-success-description',
            { messageGroupName }
          )}
          variant="success"
        ></KatAlert>
      )}

      {/* Header section with title, description, and dropdown */}
      <div className="source-review-header-container">
        <KatRow id={props.id + '-header'}>
          <div>
            <h2 id={props.id + '-title'}>
              {getTitle()}
              {getTitleToolTip()}
            </h2>
          </div>
          <div>
            <p id={props.id + '-description'}>{getDescription()}</p>
          </div>
        </KatRow>

        {/* Review status dropdown */}
        <div className="source-review-dropdown-container">
          <KatDropdown
            id={props.id + '-status-dropdown'}
            label="Review Status"
            value={reviewStatus}
            options={REVIEW_STATUS_OPTIONS}
            onChange={(event: KatDropdown.ChangeEvent) => {
              // Update review status when dropdown selection changes
              setReviewStatus(event.detail.value as ReviewStatus);
              // Hide success alert when switching to completed reviews
              if (event.detail.value === ReviewStatus.COMPLETED) {
                setShowSuccessAlert(false);
              }
            }}
          />
        </div>
      </div>
      {/* Render reviews table based on selected review status */}
      {renderReviewsTable()}
    </section>
  );
};

export default SourceReview;
